import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CalendarDays, CheckCircle, Circle, Inbox, SlidersHorizontal, X } from 'lucide-react';
import type { InboxDateFilter, InboxFilterState, InboxReadFilter } from '../services/messageService';
import {
  HorizontalFilterPillRow,
  type HorizontalFilterPillRowTheme
} from './shared/HorizontalFilterPillRow';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import {
  simpleFilterClearBtnClass,
  simpleFilterCountBadgeClass,
  simpleFilterHeaderClass,
  simpleFilterIconShellClass,
  simpleFilterIdleBadgeClass,
  simpleFilterPanelShellClass,
  simpleFilterPillTheme,
  simpleFilterSubtitleClass,
  simpleFilterTitleClass
} from './shared/simpleFilterChrome';

export const INBOX_READ_FILTER_ORDER: InboxReadFilter[] = ['all', 'read', 'unread'];
export const INBOX_DATE_FILTER_ORDER: InboxDateFilter[] = ['all', 'week', 'month', 'sixMonths'];

const READ_FILTER_KEYS: Record<InboxReadFilter, string> = {
  all: 'all',
  read: 'read',
  unread: 'unread',
};

const DATE_FILTER_KEYS: Record<InboxDateFilter, string> = {
  all: 'allTime',
  week: 'lastWeek',
  month: 'lastMonth',
  sixMonths: 'last6Months',
};

export interface InboxFiltersPanelProps {
  filters: InboxFilterState;
  onReadFilterChange: (read: InboxReadFilter) => void;
  onDateFilterChange: (date: InboxDateFilter) => void;
  className?: string;
}

const inboxFilterTheme: HorizontalFilterPillRowTheme = {
  rowClass:
    'rounded-lg border border-cyan-100/75 bg-gradient-to-br from-cyan-50/40 via-white/30 to-transparent px-2 py-1.5 dark:border-cyan-800/30 dark:from-cyan-950/20 dark:via-transparent',
  labelIconShellClass:
    'flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-cyan-400/25 to-purple-400/20 sm:h-6 sm:w-6',
  labelIconClass: 'text-cyan-700 dark:text-cyan-200',
  labelTextClass:
    'whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-cyan-800/75 dark:text-cyan-200/70',
  trackShellClass:
    'min-w-0 flex-1 overflow-hidden rounded-full border border-cyan-200/65 bg-gradient-to-r from-cyan-50/95 via-white/90 to-purple-50/80 shadow-[inset_0_1px_2px_rgba(6,182,212,0.08)] dark:border-cyan-700/50 dark:from-cyan-950/50 dark:via-slate-900/80 dark:to-purple-950/35',
  pillActiveClass: 'text-white',
  pillInactiveClass:
    'text-cyan-800/85 hover:text-cyan-950 dark:text-cyan-100/80 dark:hover:text-white',
  scrollBtnClass:
    'border-cyan-200/55 bg-white/60 text-cyan-700 hover:bg-cyan-50/90 dark:border-cyan-600/40 dark:bg-slate-900/50 dark:text-cyan-200 dark:hover:bg-slate-800/70',
  indicatorClass:
    'bg-gradient-to-r from-cyan-600 to-purple-600 shadow-sm shadow-cyan-500/25 dark:from-cyan-500 dark:to-purple-500'
};

const panelShellClass =
  'overflow-hidden rounded-xl border border-cyan-200/70 bg-white/75 shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_4px_20px_-8px_rgba(6,182,212,0.25)] ring-1 ring-cyan-100/80 backdrop-blur-sm dark:border-cyan-700/45 dark:bg-slate-900/55 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] dark:ring-cyan-800/35';

export function countActiveInboxFilters(filters: InboxFilterState): number {
  return (filters.read !== 'all' ? 1 : 0) + (filters.date !== 'all' ? 1 : 0);
}

export const InboxFiltersPanel: React.FC<InboxFiltersPanelProps> = ({
  filters,
  onReadFilterChange,
  onDateFilterChange,
  className = ''
}) => {
  const { t } = useTranslation('messages');
  const [homeDesign] = useLearnerHomeDesign();
  const simple = homeDesign === 'mme';
  const pillTheme: HorizontalFilterPillRowTheme = simple ? simpleFilterPillTheme : inboxFilterTheme;

  const readOptions = useMemo(
    () =>
      INBOX_READ_FILTER_ORDER.map((value) => ({
        value,
        label: t(READ_FILTER_KEYS[value]),
      })),
    [t]
  );

  const dateOptions = useMemo(
    () =>
      INBOX_DATE_FILTER_ORDER.map((value) => ({
        value,
        label: t(DATE_FILTER_KEYS[value]),
      })),
    [t]
  );

  const activeFilterCount = countActiveInboxFilters(filters);
  const hasActiveFilters = activeFilterCount > 0;

  const clearAll = () => {
    onReadFilterChange('all');
    onDateFilterChange('all');
  };

  const statusIcon =
    filters.read === 'read' ? CheckCircle : filters.read === 'unread' ? Circle : Inbox;

  return (
    <>
      <style>{`
        .horizontal-filter-pill-strip {
          scrollbar-width: none;
          scroll-snap-type: x proximity;
          -webkit-overflow-scrolling: touch;
        }
        .horizontal-filter-pill-strip::-webkit-scrollbar {
          height: 4px;
        }
        .horizontal-filter-pill-strip::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 3px;
        }
        .horizontal-filter-pill-strip button {
          scroll-snap-align: center;
        }
      `}</style>
      <div className={`${simple ? simpleFilterPanelShellClass : panelShellClass} ${className}`}>
        <div className={simple ? simpleFilterHeaderClass : 'flex items-center justify-between gap-3 border-b border-cyan-100/90 bg-gradient-to-r from-cyan-50/80 via-white/60 to-purple-50/50 px-3 py-2 dark:border-cyan-800/50 dark:from-cyan-950/40 dark:via-slate-900/50 dark:to-purple-950/30 sm:px-4 sm:py-2.5'}>
          <div className="flex min-w-0 items-center gap-2">
            <span className={simple ? simpleFilterIconShellClass : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-purple-500 text-white shadow-sm shadow-cyan-500/30 sm:h-8 sm:w-8'}>
              <SlidersHorizontal className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className={simple ? simpleFilterTitleClass : 'text-xs font-semibold text-cyan-900 dark:text-cyan-50 sm:text-sm'}>{t('filterTitle')}</p>
              <p className={simple ? simpleFilterSubtitleClass : 'text-[10px] text-cyan-700/65 dark:text-cyan-200/60'}>{t('filterSubtitle')}</p>
            </div>
          </div>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearAll}
              className={simple ? simpleFilterClearBtnClass : 'inline-flex shrink-0 items-center gap-1 rounded-lg border border-cyan-200/80 bg-white/90 px-2 py-1 text-[11px] font-semibold text-cyan-800 shadow-sm transition-colors hover:bg-cyan-50 dark:border-cyan-600/50 dark:bg-slate-800/80 dark:text-cyan-100 dark:hover:bg-cyan-900/60 sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-xs'}
            >
              <X className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
              {t('clearFilters')}
              <span className={simple ? simpleFilterCountBadgeClass : 'rounded-full bg-cyan-500 px-1.5 py-0.5 text-[9px] font-bold text-white tabular-nums sm:text-[10px]'}>
                {activeFilterCount}
              </span>
            </button>
          ) : (
            <span className={simple ? simpleFilterIdleBadgeClass : 'shrink-0 rounded-full border border-cyan-200/60 bg-cyan-50/80 px-2 py-0.5 text-[9px] font-medium text-cyan-700/70 dark:border-cyan-700/40 dark:bg-cyan-950/40 dark:text-cyan-300/70 sm:text-[10px]'}>
              {t('allMessages')}
            </span>
          )}
        </div>

        <div className="space-y-1.5 p-2 sm:space-y-2 sm:p-3">
          <HorizontalFilterPillRow
            label={t('statusLabel')}
            icon={statusIcon}
            options={readOptions}
            active={filters.read}
            onChange={(v) => onReadFilterChange(v ?? 'all')}
            theme={pillTheme}
          />
          <HorizontalFilterPillRow
            label={t('dateLabel')}
            icon={CalendarDays}
            options={dateOptions}
            active={filters.date}
            onChange={(v) => onDateFilterChange(v ?? 'all')}
            theme={pillTheme}
          />
        </div>
      </div>
    </>
  );
};

export function describeInboxFilters(filters: InboxFilterState, t: TFunction<'messages'>): string {
  const parts: string[] = [];
  if (filters.read === 'unread') parts.push(t('unread'));
  else if (filters.read === 'read') parts.push(t('read'));
  if (filters.date !== 'all') {
    parts.push(t(DATE_FILTER_KEYS[filters.date]).toLowerCase());
  }
  return parts.join(' ');
}
